import { FormEvent, useState } from 'react';
import { ArrowRight, CheckCircle2, LockKeyhole, Mic2, ShieldCheck } from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { unlock } from '../lib/api';
import { loginAccount } from '../canntalk/identityApi';

export default function AccessPage({ onUnlocked }: { onUnlocked: () => Promise<void> }) {
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<'account' | 'code'>('account');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true); setError('');
    try {
      if (mode === 'account') await loginAccount(email, password);
      else await unlock(code);
      setPassword(''); setCode('');
      await onUnlocked();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '未能登入。Please try again.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="access-page">
      <section className="access-story">
        <BrandMark large />
        <div className="access-copy">
          <h1>由開口，<br />到交流。</h1>
          <h2>Speak Cantonese with confidence.</h2>
          <p>揀一個生活情境，同一至三位虛擬伙伴，用廣東話傾出你嘅想法。</p>
          <p>Practise Cantonese with a group of virtual conversation partners.</p>
        </div>
        <div className="access-points">
          <span><Mic2 /> 小組口語練習 <small>Group speaking practice</small></span>
          <span><CheckCircle2 /> 重溫、學習、再練習 <small>Reflect, learn & return</small></span>
        </div>
      </section>
      <section className="access-panel">
        <div className="access-card">
          <span className="access-lock"><LockKeyhole /></span>
          <h2>歡迎返嚟</h2>
          <h3>Welcome to CanNTalk</h3>
          <p>登入你嘅個人帳戶，繼續練習同查看學習紀錄。</p>
          <div className="cnt-login-tabs" role="tablist" aria-label="登入方式 Sign-in method"><button type="button" role="tab" aria-selected={mode === 'account'} disabled={pending} onClick={() => { setMode('account'); setError(''); }}>個人帳戶 Account</button><button type="button" role="tab" aria-selected={mode === 'code'} disabled={pending} onClick={() => { setMode('code'); setError(''); }}>體驗／初始設定 Access code</button></div>
          <form onSubmit={submit}>
            {mode === 'account' ? <><label htmlFor="account-email">電郵地址 <span>Email address</span></label><input id="account-email" value={email} onChange={event => setEmail(event.target.value)} type="email" autoComplete="username" required maxLength={254} autoFocus disabled={pending} /><label htmlFor="account-password">密碼 <span>Password</span></label><input id="account-password" value={password} onChange={event => setPassword(event.target.value)} type="password" autoComplete="current-password" required maxLength={128} disabled={pending} /><small className="cnt-login-help">未有帳戶或忘記密碼？請聯絡管理員建立帳戶或重設臨時密碼。Contact your administrator for account access or a password reset.</small></> : <>
            <label htmlFor="access-code">評審存取碼 <span>Reviewer access code</span></label>
            <input id="access-code" value={code} onChange={(event) => setCode(event.target.value)} type="password" autoComplete="current-password" placeholder="••••••••••••" required disabled={pending} /><small className="cnt-login-help">每次體驗碼登入會建立新工作階段，登出或到期後無法返回該次歷史。學生及教師請用個人帳戶保存紀錄。Each review sign-in starts a new isolated session; use an individual account for persistent history.</small></>}
            {error ? <div className="form-error" role="alert">{error}</div> : null}
            <button className="primary-button full" disabled={pending || (mode === 'code' ? !code : !email || !password)}>
              {pending ? '正在驗證 Verifying…' : '進入學習平台 Enter platform'} <ArrowRight size={19} />
            </button>
          </form>
          <div className="privacy-note"><ShieldCheck /><span><b>你的練習，你的紀錄 · Your practice records</b><small>平台提供即時 AI 對話及粵語聲音，亦保留預設示範。個人練習紀錄按帳戶保存於伺服器，只供本人、獲分配教師及管理員按權限查看。角色圖像為靜態合成照片；回饋暫未提供經驗證嘅語音能力評分。</small></span></div>
          {import.meta.env.DEV ? <p className="local-demo-hint">Local demo code: <code>canntalk-demo</code></p> : null}
        </div>
      </section>
    </div>
  );
}
